import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Paper,
  Button,
  IconButton,
  InputAdornment,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemText,
  Popover,
  Radio,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { toast } from 'react-toastify';
import {
  V2WorkflowS3Link,
  getMetaLocation,
  getMetaAdvancedTargeting,
  searchSavedAudience,
  MetaAdsReachEstimate,
  saveAudience,
  publishMetaAds,
  getUserAccounts,
  modifyLOCCredit,
  MetaAdsAudienceEstimate,
} from '../../../Services'; // Updated import
import colors from '../../../Utils/colors';
import { useSelector } from 'react-redux'; // Added for auth token
import { RiDeleteBinLine } from 'react-icons/ri';
import CheerioButton from '../../../Components/CheerioButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useParams, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import images from '../../../Utils/images';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import { eventsend } from '../../../Config/analyticsFunctions';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import EmojiPicker from 'emoji-picker-react';
import { CiGlobe } from 'react-icons/ci';
import { FaWhatsapp } from 'react-icons/fa';
import { AiOutlineLike } from 'react-icons/ai';
import { FaRegComment } from 'react-icons/fa';
import { PiShareFat } from 'react-icons/pi';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';

import { format, isToday, isBefore, addDays } from 'date-fns';

const LocationSelector = ({ selectedLocations, setSelectedLocations }) => {
  const [searchText, setSearchText] = useState('');
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const authtoken = useSelector((state) => state.main.auth_token);
  const location = useLocation();
  const isChildAdAccount = location.state?.isChildAdAccount;

  // Fetch locations when search text changes
  useEffect(() => {
    const fetchLocations = async () => {
      if (searchText.length > 2) {
        setIsLoading(true);
        try {
          const data = {
            searchText,
            isChildAdAccount,
          };
          const response = await getMetaLocation(authtoken, data);
          if (response?.flag && response?.data) {
            console.log('Fetched locations:', response?.data);
            setLocations(response?.data);
          }
        } catch (error) {
          console.error('Error fetching locations:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setLocations([]);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchLocations();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText, authtoken]);

  const handleSelectLocation = (location) => {
    if (!selectedLocations?.some((loc) => loc?.key === location?.key)) {
      setSelectedLocations([...selectedLocations, location]);
    }
    setSearchText('');
    setLocations([]);
  };

  const handleRemoveLocation = (locationKey) => {
    setSelectedLocations(selectedLocations?.filter((loc) => loc?.key !== locationKey));
  };

  const getLocationTypeDisplay = (type) => {
    switch (type.toLowerCase()) {
      case 'city':
        return 'City';
      case 'country':
        return 'Country';
      case 'region':
        return 'Region';
      case 'small_geo_area':
        return 'Area';
      case 'neighborhood':
        return 'Neighborhood';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Typography variant="subtitle2" fontWeight="500" sx={{ mb: 1 }}>
        Select location
      </Typography>

      {/* Search input */}
      <TextField
        fullWidth
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Search cities, regions, countries"
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
                    stroke="#6E56CF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M22 22L20 20" stroke="#6E56CF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            height: '40px',
          },
          marginBottom: '8px',
        }}
      />

      {isLoading && (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Loading locations...
        </Typography>
      )}

      {/* Search results dropdown - MOVED BEFORE selected locations */}
      {locations?.length > 0 && searchText?.length > 0 && (
        <Box
          sx={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            bgcolor: 'white',
            maxHeight: '300px',
            overflowY: 'auto',
            width: '100%',
            zIndex: 1000,
            border: '1px solid #e0e0e0',
            position: 'absolute',
            top: isLoading ? '100px' : '80px', // Adjusted position to appear right after the search input
            left: 0,
            mt: 0.5,
          }}
        >
          {locations?.map((location) => (
            <Box
              key={location?.key}
              sx={{
                px: 2,
                py: 1.5,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onClick={() => handleSelectLocation(location)}
            >
              <Typography variant="body2">{location?.country_name ? `${location?.name}, ${location?.country_name}` : location?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {getLocationTypeDisplay(location?.type)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Selected locations */}
      {selectedLocations?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            mt: 1,
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            padding: '4px 0',
          }}
        >
          {selectedLocations?.map((location) => (
            <Box
              key={location?.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
              }}
            >
              <Typography variant="body2">{location?.country_name ? `${location?.name}, ${location?.country_name}` : location?.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {getLocationTypeDisplay(location?.type)}
                </Typography>
                <IconButton size="small" onClick={() => handleRemoveLocation(location?.key)} sx={{ ml: 1, p: 0.2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const AdvancedTargeting = ({ selectedTags, setSelectedTags }) => {
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  // const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const authtoken = useSelector((state) => state.main.auth_token);
  const { adId } = useParams();
  const location = useLocation();
  const isChildAdAccount = location.state?.isChildAdAccount;
  const adAccountId = `act_${adId}`;
  const [openAdvancedTargetingDialog, setOpenAdvancedTargetingDialog] = useState(false);
  // Fetch suggestions when search text changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      const data = {
        searchText,
        adAccountId,
        isChildAdAccount,
      };
      if (searchText.length > 2 && adAccountId) {
        setIsLoading(true);
        try {
          const response = await getMetaAdvancedTargeting(authtoken, data);
          if (response && response?.flag && response?.data) {
            console.log('Fetched suggestions:', response?.data);
            setSuggestions(response?.data);
          }
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchSuggestions();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText, authtoken, adAccountId]);

  const handleSelectSuggestion = (suggestion) => {
    if (!selectedTags?.some((tag) => tag?.id === suggestion?.id)) {
      setSelectedTags([...selectedTags, suggestion]);
    }
    setSearchText('');
    setSuggestions([]);
  };

  const handleRemoveTag = (tagId) => {
    setSelectedTags(selectedTags?.filter((tag) => tag?.id !== tagId));
  };

  // Map category types to colors
  const getCategoryColor = (path) => {
    // Check if type has path and use first element
    if (path && path?.length > 0) {
      switch (path[0]) {
        case 'Interests':
          return '#5B00B5'; // Light orange
        case 'Demographics':
          return '#E39F01'; // Light purple
        case 'Behaviours':
          return '#33A602'; // Light green
        default:
          return 'black';
      }
    }
    return 'black'; // Default if no path
  };

  // Get category display name
  const getCategoryName = (path) => {
    // Use first element of path if available
    if (path && path?.length > 0) {
      return path[0];
    }
    return 'Interests'; // Default
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Advanced Targeting
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Use advanced targeting techniques to narrow down your niche and improve results.
        <Box
          component="span"
          sx={{ color: '#000000', fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => setOpenAdvancedTargetingDialog(true)}
        >
          Learn more
        </Box>
        <Dialog
          open={openAdvancedTargetingDialog}
          onClose={() => setOpenAdvancedTargetingDialog(false)}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              width: '700px',
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              fontSize: 20,
              pb: 0.5,
              borderBottom: 'none',
              position: 'relative',
            }}
          >
            About Advance Retargeting
            <IconButton
              onClick={() => setOpenAdvancedTargetingDialog(false)}
              sx={{
                position: 'absolute',
                right: 16,
                top: 20,
                p: 0,
              }}
            >
              <img src={images.CROSS_BLACK} alt="Close" width={20} height={20} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 3 }}>
            {/* First Info Section */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
              <InfoOutlineIcon sx={{ color: '#000', mr: 1, mt: '2px' }} fontSize="small" />
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                Reach the right people, at the right time, with the right message. Our advanced targeting tools help you segment your audience based on key
                demographics like age, gender, language, and location—ensuring every message feels personalized. Go deeper with behavioral filters such as
                recent activity, device type, or purchase intent to fine-tune your campaigns for maximum impact. Whether you're running promotions,
                re-engagement flows, or platform-specific offers, these targeting options help drive better ROI with minimal effort.
              </Typography>
            </Box>

            {/* What does different types tags mean Section */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
              What does different types tags mean
            </Typography>

            {/* Interests Section */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
              <InfoOutlineIcon sx={{ color: '#000', mr: 1, mt: '2px' }} fontSize="small" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Interests
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5, mb: 2 }}>
                  Understand what your audience cares about. With interest-based targeting, you can reach users who are passionate about categories like
                  fashion, fitness, finance, and more. Whether it's shopping habits or hobby preferences, use these cues to personalize campaigns that truly
                  resonate. Few shot examples of interest categories are:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <li>
                    <Typography variant="body2">Fitness & Wellness</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Online Shopping</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Technology Enthusiasts</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Food & Recipes</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Personal Finance</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Travel Buffs</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Fashion Trends</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Parenting Tips</Typography>
                  </li>
                </Box>
              </Box>
            </Box>

            {/* Behavior Section */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
              <InfoOutlineIcon sx={{ color: '#000', mr: 1, mt: '2px' }} fontSize="small" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Behavior
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5, mb: 2 }}>
                  Target users based on how they act, not just who they are. Behavioral targeting lets you connect with users based on actions like website
                  visits, purchase history, device usage, or engagement level. It's ideal for re-engagement flows, upselling, or reminding users of what they
                  love. Few shot examples of behavior categories are:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <li>
                    <Typography variant="body2">Recent Purchasers</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Cart Abandoners</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">High Email Open Rate</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Mobile Device Users</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Inactive Past 30 Days</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Downloaded Mobile App CTA</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Frequent Visitors</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">New Sign-ups</Typography>
                  </li>
                </Box>
              </Box>
            </Box>

            {/* Demographics Section */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
              <InfoOutlineIcon sx={{ color: '#000', mr: 1, mt: '2px' }} fontSize="small" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Demographics
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5, mb: 2 }}>
                  Reach people based on who they are. Demographic filters like age, gender, language, and education help you shape relevant messages for
                  specific audience groups. Whether you're tailoring promotions for college students or launching a campaign for new parents, demographic
                  targeting gives you precision at scale. Few shot examples of demographics are:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <li>
                    <Typography variant="body2">Age 25-34</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Gender Female</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Language Hindi</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">City Bengaluru</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Education Graduate</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Income High Income</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Profession IT Professional</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Marital Status Married</Typography>
                  </li>
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Typography>
      {/* Search input */}
      <Box sx={{ position: 'relative', mb: 2 }}>
        <TextField
          fullWidth
          value={searchText}
          onChange={(e) => {
            const value = e.target.value;
            setSearchText(value);
            eventsend('Advance_targeting_search_bar_clicked', { searchText: value });
          }}
          placeholder="Search Interests, Demographics or Behaviors"
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
                      stroke="#000"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M22 22L20 20" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          }}
        />

        {suggestions?.length > 0 && searchText?.length > 0 && (
          <Box
            sx={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              borderRadius: '8px',
              bgcolor: 'white',
              maxHeight: '300px',
              overflowY: 'auto',
              width: '100%',
              zIndex: 1000,
              border: '1px solid #e0e0e0',
              position: 'absolute',
              top: '100%',
              left: 0,
              mt: 0.5,
            }}
          >
            {suggestions?.map((suggestion) => {
              const category = getCategoryName(suggestion?.path);
              const bgColor = getCategoryColor(suggestion?.path);
              const tooltipContent = (
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {suggestion?.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {suggestion?.type?.replace(/_/g, ' ')}
                  </Typography>
                  {suggestion?.audience_size_lower_bound && suggestion?.audience_size_upper_bound && (
                    <Typography variant="body2">
                      <strong>Audience:</strong> {suggestion?.audience_size_lower_bound.toLocaleString()} -{' '}
                      {suggestion?.audience_size_upper_bound.toLocaleString()}
                    </Typography>
                  )}
                </Box>
              );
              return (
                <Tooltip key={suggestion?.id} title={tooltipContent} arrow placement="right">
                  <Box
                    key={suggestion?.id}
                    sx={{
                      px: 2,
                      py: 1.5,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    <Typography variant="body2">{suggestion?.name}</Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        color: bgColor,
                        display: 'inline-flex',
                        fontSize: '12px',
                        border: `1px solid ${bgColor}`,
                      }}
                    >
                      {category}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Selected tags */}
      {selectedTags?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            mb: 2,
          }}
        >
          {selectedTags?.map((tag) => {
            const category = getCategoryName(tag?.path);
            const bgColor = getCategoryColor(tag?.path);

            return (
              <Box
                key={tag?.id}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  borderRadius: 2,
                  color: bgColor,
                  border: `1px solid ${bgColor}`,
                }}
              >
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {tag?.name}
                </Typography>
                <IconButton size="small" onClick={() => handleRemoveTag(tag?.id)} sx={{ p: 0.2 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Search results dropdown */}

      {isLoading && (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Loading suggestions...
        </Typography>
      )}
    </Box>
  );
};

const CreateMetaAd = () => {
  const navigate = useNavigate();
  const authtoken = useSelector((state) => state.main.auth_token); // Get auth token from Redux
  const localdata = useSelector((state) => state.main.localdata); // Get local data from Redux

  //TEMP OPENING MESSAGE
  const [selectedDestinations, setSelectedDestinations] = useState(['whatsapp']); // ['whatsapp', 'messenger', 'instagram']
  const [selectedObjective, setSelectedObjective] = useState(''); // 'purchase', 'lead', 'traffic'
  const handleDestinationChange = (destination) => {
    if (selectedDestinations.includes(destination)) {
      setSelectedDestinations(selectedDestinations.filter((dest) => dest !== destination));
    } else {
      setSelectedDestinations([...selectedDestinations, destination]);
    }
  };

  const destinations = [
    {
      id: 'messenger',
      name: 'Messenger',
      subtitle: '',
      icon: <img src={images.Messenger} alt="messenger" width={32} height={32} style={{ background: 'purple' }} />,
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    },
    {
      id: 'instagram',
      name: 'Instagram',
      subtitle: '',
      icon: <img src={images.WFInstagram} alt="instagram" width={32} height={32} style={{ background: 'purple', borderRadius: '50%' }} />,
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      subtitle: '',
      icon: <img src={images.WhatsAppWhite} alt="whatsapp" width={32} height={32} style={{ background: 'green', borderRadius: '50%' }} />,
    },
  ];

  const objectives = [
    {
      value: 'OUTCOME_SALES',
      title: 'Purchase Optimization',
      subtitle: 'Show your ads to people most likely to buy your product—maximizing conversions and sales from your budget',
    },
    {
      value: 'OUTCOME_ENGAGEMENT',
      title: 'Lead Optimization',
      subtitle: 'Target people most likely to submit their details, helping you collect more qualified leads at a lower cost',
    },
    {
      value: 'OUTCOME_TRAFFIC',
      title: 'Traffic Optimization',
      subtitle: 'Drive more visitors to your website or landing page by showing ads to people most likely to click',
    },
  ];

  // Log the selected ad account
  const [expandedSections, setExpandedSections] = useState({
    adCreative: true, // Start with ad creative expanded
    audience: false,
    budget: false,
    openingMessage: false,
  });
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [adData, setAdData] = useState({
    adName: '',
    headline: '',
    adCaption: '',
    footer: '',
    mediaType: 'Image', // Default to Image
    imageUrl: '', // For Image type
    videoUrl: '', // For Video type
    thumbnailUrl: '', // For Video type thumbnail
  });
  const [audienceData, setAudienceData] = useState({
    location: selectedLocations,
    gender: 'ALL',
    minAge: 18,
    maxAge: 65,
    platforms: ['instagram', 'facebook'], // Default selected platforms
    targeting: selectedTags,
  });
  const [adStartDate, setAdStartDate] = useState(new Date());
  const today = new Date();
  const [adEndDate, setAdEndDate] = useState(() => {
    const oneWeekLater = new Date(today);
    oneWeekLater.setDate(oneWeekLater.getDate() + 7);
    return oneWeekLater;
  });
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [selectedSavedAudience, setSelectedSavedAudience] = useState('');
  const [isLoadingSavedAudiences, setIsLoadingSavedAudiences] = useState(false);
  const { adId } = useParams();
  const adAccountId = `act_${adId}`;
  const [reachEstimate, setReachEstimate] = useState(null);
  const [isLoadingReach, setIsLoadingReach] = useState(false);
  const [dailyBudget, setDailyBudget] = useState(
    localdata?.walletCurrency === 'INR' ? 250 : 5
  );

  const [userAccounts, setUserAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [formIsValid, setFormIsValid] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [audienceName, setAudienceName] = useState('');
  const [audienceSaved, setAudienceSaved] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  // Add these states to your component
  const [mediaDimensions, setMediaDimensions] = useState({ width: 0, height: 0 });
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [audienceEstimate, setAudienceEstimate] = useState(null);
  const [openBudgetInfoDialog, setOpenBudgetInfoDialog] = useState(false);
  const [greetingText, setGreetingText] = useState('');
  const [prefilledMessage, setPrefilledMessage] = useState('');
  const minEndDate = useMemo(() => {
    const minDate = new Date(adStartDate);
    minDate.setDate(minDate.getDate() + 7);
    return minDate;
  }, [adStartDate]);
  const location = useLocation();
  const isChildAdAccount = location.state?.isChildAdAccount;
  const currencySymbol = localdata?.walletCurrency === 'INR' ? '₹' : '$';
  const minimumDailyBudget = localdata?.walletCurrency === 'INR' ? 250 : 5;

  const adAccountName = location.state?.adAccountName || 'Facebook Page Name';
  const isFormValid =
    dailyBudget !== '' &&
    dailyBudget !== null &&
    dailyBudget !== undefined &&
    dailyBudget >= minimumDailyBudget &&
    selectedObjective !== '';
  useEffect(() => {
    if (adStartDate) {
      const newEndDate = new Date(adStartDate);
      newEndDate.setDate(newEndDate.getDate() + 7);
      setAdEndDate(newEndDate);
    }
  }, [adStartDate]);
  // Add validation function to check if all required fields are filled
  useEffect(() => {
    const isValid =
      selectedLocations?.length > 0 && audienceData?.gender && audienceData?.minAge && audienceData?.maxAge && audienceData?.platforms?.length > 0;

    setFormIsValid(isValid);
  }, [selectedLocations, audienceData, selectedTags]);

  // Modify the handleSaveAudience function to open the modal instead of saving directly
  const handleSaveAudienceName = () => {
    if (!formIsValid) return;
    setSaveModalOpen(true);
  };

  // Add function to close the modal
  const handleCloseModal = () => {
    setSaveModalOpen(false);
    setAudienceName('');
  };
  const fetchSavedAudiences = async () => {
    setIsLoadingSavedAudiences(true);
    try {
      const response = await searchSavedAudience(authtoken, {
        adAccountId: adAccountId,
        searchText: '',
      });

      if (response?.flag && response?.data) {
        setSavedAudiences(response?.data);
      } else {
        toast.error(response?.message || 'Failed to fetch saved audiences.');
      }
    } catch (error) {
      console.error('Error fetching saved audiences:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoadingSavedAudiences(false);
    }
  };
  const fetchUserAccounts = async () => {
    try {
      const response = await getUserAccounts(authtoken);

      if (response && response?.flag && Array.isArray(response?.data?.data)) {
        const accounts = response.data.data;
        setUserAccounts(accounts);

        const matchedAccount = accounts.find((account) => account?.id === localdata?.metaConfig?.leadsPageId);

        if (matchedAccount) {
          setSelectedAccountId(matchedAccount.id);
        } else if (accounts.length > 0) {
          setSelectedAccountId(accounts[0].id);
        }
      } else {
        toast.error(response?.message || 'Failed to fetch saved audiences.');
      }
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      toast.error('Failed to load user accounts', {
        position: 'top-center',
      });
    }
  };

  useEffect(() => {
    fetchUserAccounts();
  }, []);

  useEffect(() => {
    if (expandedSections?.audience) {
      fetchSavedAudiences();
    }
  }, [expandedSections?.audience]);

  const handleSavedAudienceChange = (event) => {
    const audienceId = event.target.value;
    setSelectedSavedAudience(audienceId);

    // Find the selected audience from the list
    const selectedAudience = savedAudiences?.find((audience) => audience?._id === audienceId);
    console.log(selectedAudience);
    // If an audience is selected, update your audienceData state
    if (selectedAudience) {
      console.log('Selected Audience:', selectedAudience);
      setAudienceData({
        ...audienceData,
        // Map using the correct property names from API response
        minAge: selectedAudience?.ageRangeStart || 18,
        maxAge: selectedAudience?.ageRangeEnd || 65,
        platforms: selectedAudience?.selectedPlatforms || [],
        gender: selectedAudience?.gender || 'ALL',
      });

      // For location, you need to set selectedLocations state
      if (selectedAudience?.adLocation && selectedAudience?.adLocation?.length > 0) {
        setSelectedLocations(selectedAudience?.adLocation);
      }

      // For targeting tags, set the selectedTags state
      // For targeting tags, ensure proper mapping with all required properties
      if (selectedAudience?.peopleWhoMatch && selectedAudience?.peopleWhoMatch?.length > 0) {
        setSelectedTags(
          selectedAudience?.peopleWhoMatch?.map((item) => {
            // Map attribute type to a category path
            let categoryPath = ['Interests']; // Default
            if (item?.attrType === 'Demographics') {
              categoryPath = ['Demographics'];
            } else if (item?.attrType === 'Behaviors' || item?.attrType === 'work_employers') {
              categoryPath = ['Behaviours'];
            }

            return {
              id: item?.attrId || `tag-${Math.random().toString(36).substr(2, 9)}`,
              name: item?.attrValue,
              path: categoryPath,
              // Keep original data for when you need to save
              // originalType: item.attrType,
              // originalValue: item.attrValue
            };
          })
        );
      } else {
        setSelectedTags([]);
      }
      console.log(selectedTags);
    }
  };

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const currentCaption = adData?.adCaption || '';
    const newCaption = currentCaption.slice(0, cursorPosition) + emoji + currentCaption.slice(cursorPosition);

    // Update your adData state here
    handleChange({
      target: {
        name: 'adCaption',
        value: newCaption,
      },
    });

    setEmojiAnchorEl(null);
  };

  const handleEmojiButtonClick = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleTextFieldClick = (event) => {
    setCursorPosition(event.target.selectionStart);
  };

  const handleTextFieldKeyUp = (event) => {
    setCursorPosition(event.target.selectionStart);
  };

  const handlePlatformChange = (event) => {
    const {
      target: { value },
    } = event;

    setAudienceData({
      ...audienceData,
      platforms: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleAudienceChange = (e) => {
    const { name, value } = e.target;
    setAudienceData({
      ...audienceData,
      [name]: value,
    });
  };

  const handleFromAgeChange = (event) => {
    setAudienceData({
      ...audienceData,
      minAge: event.target.value,
    });
  };

  const handleToAgeChange = (event) => {
    setAudienceData({
      ...audienceData,
      maxAge: event.target.value,
    });
  };
  const iconMap = {
    Like: <AiOutlineLike style={{ fontSize: 18 }} />,
    Comment: <FaRegComment style={{ fontSize: 16 }} />,
    Share: <PiShareFat style={{ fontSize: 18 }} />,
  };
  const getAdaptiveHeight = () => {
    if (!isMediaLoaded || !mediaDimensions.width || !mediaDimensions.height) {
      return 250; // Default
    }

    const aspectRatio = mediaDimensions.height / mediaDimensions.width;
    const containerWidth = document.getElementById('media-preview')?.offsetWidth || 400;

    const calculatedHeight = containerWidth * aspectRatio;

    return Math.min(Math.max(calculatedHeight, 100), 1000); // Use bounds if needed
  };
  // Image upload related states
  const [upFile, setUpFile] = useState(null);
  const [upFile64, setUpFile64] = useState('');
  const [upFileName, setUpFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [s3Link, setS3Link] = useState(null);
  const upImageRef = useRef();
  const [imageUrl, setImageUrl] = useState('');
  const areRequiredFieldsFilled = () => {
    return (
      adData?.adName?.trim() !== '' &&
      adData?.adCaption?.trim() !== '' &&
      adData?.footer?.trim() !== '' &&
      ((adData?.mediaType === 'Image' && adData?.imageUrl !== '') || (adData?.mediaType === 'Video' && adData?.videoUrl !== '' && adData?.thumbnailUrl !== ''))
    );
  };

  // Add this to track if the section is completed
  const [sectionCompleted, setSectionCompleted] = useState({
    adCreative: false,
    audience: false,
    budget: false,
    openingMessage: false,
    performanceOptimization: false,
  });
  // Add new state variables for the thumbnail
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailFile64, setThumbnailFile64] = useState('');
  const [thumbnailFileName, setThumbnailFileName] = useState('');
  const [thumbnailS3Link, setThumbnailS3Link] = useState(null);
  const thumbnailImageRef = useRef();

  const getMediaDimensions = (src, mediaType) => {
    if (mediaType === 'Video') {
      const video = document.createElement('video');
      video.src = src;
      video.onloadedmetadata = () => {
        setMediaDimensions({ width: video.videoWidth, height: video.videoHeight });
        setIsMediaLoaded(true);
      };
    } else {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setMediaDimensions({ width: img.width, height: img.height });
        setIsMediaLoaded(true);
      };
    }
  };

  useEffect(() => {
    if (s3Link) {
      setIsMediaLoaded(false);
      getMediaDimensions(s3Link, adData?.mediaType);
    } else if (thumbnailS3Link && adData?.mediaType === 'Video') {
      setIsMediaLoaded(false);
      getMediaDimensions(thumbnailS3Link, 'Image');
    }
  }, [s3Link, thumbnailS3Link, adData?.mediaType]);

  const calculateAdaptiveHeight = () => {
    if (!isMediaLoaded || !mediaDimensions.width || !mediaDimensions.height) {
      return 250; // Default height
    }

    const aspectRatio = mediaDimensions.height / mediaDimensions.width;
    const containerWidth = 400; // Adjust based on your container width
    const calculatedHeight = containerWidth * aspectRatio;

    // Set min and max bounds for better UX
    const minHeight = 200;
    const maxHeight = 500;

    return Math.min(Math.max(calculatedHeight, minHeight), maxHeight);
  };

  const toggleSection = (section) => {
    // If closing the adCreative section and all fields are filled, mark it as completed
    if (section === 'adCreative' && expandedSections[section] && areRequiredFieldsFilled()) {
      setSectionCompleted({
        ...sectionCompleted,
        adCreative: true,
      });
    }

    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const handleNextClick = () => {
    if (areRequiredFieldsFilled()) {
      setSectionCompleted({
        ...sectionCompleted,
        adCreative: true,
      });
      toggleSection('adCreative');
      // Optionally, you can auto-expand the next section
      setExpandedSections({
        ...expandedSections,
        adCreative: false,
        audience: true,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mediaType') {
      // Clear uploads and thumbnail first
      deleteMedia();
      if (value === 'Image') {
        deleteThumbnail(); // Only delete thumbnail when switching to Image
      }

      // Then update state
      setAdData({
        ...adData,
        mediaType: value,
        imageUrl: '',
        videoUrl: '',
        thumbnailUrl: '',
      });
    } else {
      setAdData({
        ...adData,
        [name]: value,
      });
    }

    console.log('Field changed:', name, value, adData);
  };

  // Update adData when s3Link changes
  useEffect(() => {
    if (s3Link) {
      if (adData?.mediaType === 'Image') {
        setAdData({
          ...adData,
          imageUrl: s3Link,
        });
      } else {
        setAdData({
          ...adData,
          videoUrl: s3Link,
        });
      }
    }
  }, [s3Link]);

  useEffect(() => {
    if (thumbnailS3Link) {
      setAdData({
        ...adData,
        thumbnailUrl: thumbnailS3Link,
      });
    }
  }, [thumbnailS3Link]);

  // Trigger S3 upload when upFile64 changes
  useEffect(() => {
    if (upFile64) {
      uploadToS3();
    }
  }, [upFile64]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const UploadValidator = (file, type) => {
    if (type === 'Image') {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
      if (!validTypes.includes(file?.type)) {
        toast.error('Only JPG, PNG and GIF images are allowed', {
          position: 'top-center',
        });
        return false;
      }

      // 5MB size limit
      if (file?.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB', {
          position: 'top-center',
        });
        return false;
      }

      return true;
    } else if (type === 'Video') {
      const validTypes = ['video/mp4', 'video/quicktime', 'video/mpeg', 'video/webm'];
      if (!validTypes?.includes(file.type)) {
        toast.error('Only MP4, MOV, MPEG and WebM videos are allowed', {
          position: 'top-center',
        });
        return false;
      }

      // 100MB size limit for videos
      if (file?.size > 25 * 1024 * 1024) {
        toast.error('File size should be less than 25MB', {
          position: 'top-center',
        });
        return false;
      }

      return true;
    }
    return false;
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (UploadValidator(file, 'Image')) {
        setThumbnailFile(file);
        setThumbnailFileName(file.name);
        const base64 = await convertToBase64(file);
        setThumbnailFile64(base64);
        // Upload to S3
        uploadThumbnailToS3(base64, file);
      }
    } else {
      toast.error('Unexpected error occurred, please try again later.', {
        position: 'top-center',
      });
    }
  };

  // Add a separate function for thumbnail uploads
  const uploadThumbnailToS3 = (base64, file) => {
    const data = {
      image: base64,
      type: file?.type?.split('/')[0],
      format: file?.type?.split('/')[1],
    };

    toast.promise(
      V2WorkflowS3Link(data, authtoken),
      {
        pending: 'Uploading thumbnail image...',
        success: {
          render({ data }) {
            if (data && data?.data && data?.data?.url) {
              setThumbnailS3Link(data?.data?.url);
              return 'Thumbnail uploaded successfully!';
            } else {
              throw new Error('Failed to get URL from response');
            }
          },
          icon: '🟢',
          autoClose: true,
          isLoading: false,
        },
        error: {
          render({ data }) {
            console.error('S3 upload error:', data);
            return 'Error uploading thumbnail, please try again later.';
          },
          icon: '🔴',
          autoClose: true,
          isLoading: false,
        },
      },
      {
        position: 'top-center',
      }
    );
  };

  // Add a function to delete the thumbnail
  const deleteThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailFile64('');
    setThumbnailFileName('');
    setThumbnailS3Link(null);
    setAdData({
      ...adData,
      thumbnailUrl: '',
    });
  };

  const handlefileupload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const mediaType = adData?.mediaType;
      if (UploadValidator(file, mediaType)) {
        setUpFile(file);
        setUpFileName(file?.name);
        const base64 = await convertToBase64(file);
        setUpFile64(base64);
        setImageUrl('');
      }
    } else {
      toast.error('Unexpected error occurred, please try again later.', {
        position: 'top-center',
      });
    }
  };

  const handleDragFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDropFile = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      const mediaType = adData?.mediaType;
      if (UploadValidator(file, mediaType)) {
        setUpFile(file);
        setUpFileName(file.name);
        const base64 = await convertToBase64(file);
        setUpFile64(base64);
        setImageUrl('');
      }
    } else {
      toast.error('Unexpected error occurred, please try again later.', {
        position: 'top-center',
      });
    }
  };

  const handleImageUrl = async (url) => {
    if (url === '') {
      return;
    }

    if (upFileName) {
      toast.error('Delete uploaded media and try again', {
        position: 'top-center',
      });
      return;
    }

    setImageUrl(url);

    try {
      const response = await fetch(url)
        .then((res) => {
          if (res?.ok) {
            return res;
          } else {
            throw new Error(res?.statusText);
          }
        })
        .catch((error) => {
          throw error;
        });

      const blob = await response.blob();
      const fileType = blob.type.includes('video') ? 'Video' : 'Image';
      const file = new File([blob], `media_from_url.${blob.type.split('/')[1]}`, { type: blob.type });

      if (UploadValidator(file, fileType)) {
        setUpFile(file);
        setUpFileName(file.name);
        const base64 = await convertToBase64(file);
        setUpFile64(base64);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Not able to fetch media. Please check your url.', {
        position: 'top-center',
      });
    }
  };

  // Unified upload function for both image and video using V2WorkflowS3Link
  const uploadToS3 = () => {
    const data = {
      image: upFile64,
      type: upFile?.type?.split('/')[0],
      format: upFile?.type?.split('/')[1],
    };

    toast.promise(
      V2WorkflowS3Link(data, authtoken),
      {
        pending: adData?.mediaType === 'Video' ? 'Uploading video...' : 'Uploading image...',
        success: {
          render({ data }) {
            if (data && data?.data && data?.data?.url) {
              setS3Link(data?.data?.url);
              return `${adData?.mediaType} uploaded successfully!`;
            } else {
              throw new Error('Failed to get URL from response');
            }
          },
          icon: '🟢',
          autoClose: true,
          isLoading: false,
        },
        error: {
          render({ data }) {
            console.error('S3 upload error:', data);
            return `Error uploading ${adData?.mediaType?.toLowerCase()}, please try again later.`;
          },
          icon: '🔴',
          autoClose: true,
          isLoading: false,
        },
      },
      {
        position: 'top-center',
      }
    );
  };

  const deleteMedia = () => {
    setUpFile(null);
    setUpFile64('');
    setUpFileName('');
    setS3Link(null);
    setImageUrl('');
    setAdData({
      ...adData,
      imageUrl: '',
    });
  };

  const handleSaveAudience = async () => {
    console.log('selectedlocations', selectedLocations);
    console.log('selectedTags', selectedTags);
    try {
      eventsend('Save_audience_clicked');
      // Process selectedLocations to get countries and regions
      // const countries = [];
      // const regions = [];
      // const cities = [];
      const interests = [];
      // selectedLocations.forEach(location => {
      //     if (location.country_name) {
      //         countries.push(location.country_name);
      //     }
      //     if (location.name) {
      //         cities.push({ name: location.name });
      //     }
      //     if (location.region && location.region_id) {
      //         regions.push({
      //             name: location.region,
      //             key: location.region_id
      //         });
      //     }
      // });

      selectedTags?.forEach((interest) => {
        if (interest?.id && interest?.name && interest?.path[0]) {
          console?.log(interest);
          interests?.push({
            id: interest?.id,
            name: interest?.name,
            type: interest?.path[0],
          });
        }
      });

      // Prepare the input data structure for the API
      const audienceInput = {
        adAccountId: adAccountId,
        adName: audienceName?.trim(),
        audienceLocation: selectedLocations,
        selectedPlatforms: audienceData?.platforms,
        gender: audienceData?.gender,
        minAge: audienceData?.minAge,
        maxAge: audienceData?.maxAge,
        advanceTargets: interests,
      };

      // Call your API function
      const response = await saveAudience(authtoken, audienceInput);

      if (response?.flag) {
        toast.success('Audience saved successfully', {
          position: 'top-center',
        });
        setSaveModalOpen(false);
        setAudienceName('');
        setAudienceSaved(true);
        // Refresh the saved audiences list
        fetchSavedAudiences();
      } else {
        toast.error(response?.message || 'Failed to save audience', {
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('Error saving audience:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save audience';
      toast.error(errorMessage, {
        position: 'top-center',
      });
    }
  };
  const handleSubmit = async () => {
    try {
      eventsend('Publish_ad_clicked');
      // Prepare the data in the format expected by your API
      if (dailyBudget < minimumDailyBudget) {
        toast.error(`Daily budget must be at least ${currencySymbol}${minimumDailyBudget}`, {
          position: 'top-center',
        });
        return;
      }

      const countries = [];
      const regions = [];
      const cities = [];
      const zips = [];
      const places = [];
      const custom_locations = [];
      const geo_markets = [];
      const electoral_districts = [];
      const country_groups = [];
      const subneighborhoods = [];
      const neighborhoods = [];
      const subcities = [];
      const metro_areas = [];
      const small_geo_areas = [];
      const medium_geo_areas = [];
      const large_geo_areas = [];
      const location_cluster_ids = [];
      const interests = [];

      selectedLocations?.forEach((location) => {
        const { _id, ...locationWithoutId } = location;

        switch (location?.type) {
          case 'country':
            countries?.push(locationWithoutId);
            break;
          case 'region':
            regions?.push(locationWithoutId);
            break;
          case 'city':
            cities?.push(locationWithoutId);
            break;
          case 'zip':
            zips?.push(locationWithoutId);
            break;
          case 'place':
            places?.push(locationWithoutId);
            break;
          case 'custom_location':
            custom_locations?.push(locationWithoutId);
            break;
          case 'geo_market':
            geo_markets?.push(locationWithoutId);
            break;
          case 'electoral_district':
            electoral_districts?.push(locationWithoutId);
            break;
          case 'country_group':
            country_groups?.push(locationWithoutId);
            break;
          case 'subneighborhood':
            subneighborhoods?.push(locationWithoutId);
            break;
          case 'neighborhood':
            neighborhoods?.push(locationWithoutId);
            break;
          case 'subcity':
            subcities?.push(locationWithoutId);
            break;
          case 'metro_area':
            metro_areas?.push(locationWithoutId);
            break;
          case 'small_geo_area':
            small_geo_areas?.push(locationWithoutId);
            break;
          case 'medium_geo_area':
            medium_geo_areas?.push(locationWithoutId);
            break;
          case 'large_geo_area':
            large_geo_areas?.push(locationWithoutId);
            break;
          case 'location_cluster_id':
            location_cluster_ids?.push(locationWithoutId);
            break;
          default:
            console.warn(`Unknown location type: ${location.type}`);
            break;
        }
      });

      const adjustedStartDate = new Date(adStartDate);
      adjustedStartDate?.setHours(12, 0, 0, 0);
      const adjustedEndDate = new Date(adEndDate);
      adjustedEndDate?.setHours(12, 0, 0, 0);
      if (adjustedEndDate < adjustedStartDate) {
        toast.error('End date must be after start date', { position: 'top-center' });
        return;
      }

      // console.log(neighborhoods, 'neighborhoods');
      const adInput = {
        adAccountId: adAccountId,
        adName: adData?.adName,
        metaPageId: selectedAccountId,
        headline: adData?.footer,
        adCaption: adData?.adCaption,
        footer: adData?.footer,
        mediaType: adData?.mediaType,
        imageUrl: adData?.imageUrl,
        videoUrl: adData?.videoUrl,
        thumbnailUrl: adData?.thumbnailUrl,
        // Include audience data
        geoLocation: {
          cities,
          countries,
          regions,
          zips,
          places,
          custom_locations,
          geo_markets,
          electoral_districts,
          country_groups,
          subneighborhoods,
          neighborhoods,
          subcities,
          metro_areas,
          small_geo_areas,
          medium_geo_areas,
          large_geo_areas,
          location_cluster_ids,
        },
        gender: audienceData?.gender,
        minAge: audienceData?.minAge,
        maxAge: audienceData?.maxAge,
        platforms: audienceData?.platforms,
        advanceTargets: selectedTags,
        // Budget data
        dailyBudget: dailyBudget, // You'll need to add this state
        adStartDate: adjustedStartDate,
        adEndDate: adjustedEndDate,
        greetingMessage: greetingText,
        autofillMessageContent: prefilledMessage,
        isChildAdAccount: isChildAdAccount,
        destinations: selectedDestinations,
        objective: selectedObjective,
      };
      const loadingToastId = toast.loading('Creating ad...', {
        position: 'top-center',
      });

      // Make API call to your backend
      const response = await publishMetaAds(authtoken, adInput);

      if (response?.flag && response?.data && response?.data?.status) {
        toast.update(loadingToastId, {
          render: 'Ad created successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
          closeButton: true,
        });
        navigate('/creator/metaads');
      } else {
        toast.update(loadingToastId, {
          render: response?.message || 'Failed to create ad',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
          closeButton: true,
        });
      }
    } catch (error) {
      console.error('Error creating ad:', error);
      toast.error(error.response?.data?.message || 'Failed to create ad', {
        position: 'top-center',
      });
    }
  };
  const formatReachEstimate = (lowerBound, upperBound) => {
    const formatNumber = (num) => {
      if (num >= 1000) {
        return Math.floor(num / 1000) + 'K';
      }
      return num;
    };

    const formattedLower = formatNumber(lowerBound);
    const formattedUpper = formatNumber(upperBound);

    return `${formattedLower} - ${formattedUpper}`;
  };

  const handleAudienceNext = async () => {
    try {
      setIsLoadingReach(true);

      // Process selectedLocations to get countries, regions, and other types
      const countries = [];
      const regions = [];
      const cities = [];
      const zips = [];
      const places = [];
      const custom_locations = [];
      const geo_markets = [];
      const electoral_districts = [];
      const country_groups = [];
      const subneighborhoods = [];
      const neighborhoods = [];
      const subcities = [];
      const metro_areas = [];
      const small_geo_areas = [];
      const medium_geo_areas = [];
      const large_geo_areas = [];
      const location_cluster_ids = [];
      const interests = [];

      selectedLocations.forEach((location) => {
        switch (location?.type) {
          case 'country':
            countries?.push(location);
            break;
          case 'region':
            regions?.push(location);
            break;
          case 'city':
            cities?.push(location);
            break;
          case 'zip':
            zips?.push(location);
            break;
          case 'place':
            places?.push(location);
            break;
          case 'custom_location':
            custom_locations?.push(location);
            break;
          case 'geo_market':
            geo_markets?.push(location);
            break;
          case 'electoral_district':
            electoral_districts?.push(location);
            break;
          case 'country_group':
            country_groups?.push(location);
            break;
          case 'subneighborhood':
            subneighborhoods?.push(location);
            break;
          case 'neighborhood':
            neighborhoods?.push(location);
            break;
          case 'subcity':
            subcities?.push(location);
            break;
          case 'metro_area':
            metro_areas?.push(location);
            break;
          case 'small_geo_area':
            small_geo_areas?.push(location);
            break;
          case 'medium_geo_area':
            medium_geo_areas?.push(location);
            break;
          case 'large_geo_area':
            large_geo_areas?.push(location);
            break;
          case 'location_cluster_id':
            location_cluster_ids?.push(location);
            break;
          default:
            console.warn(`Unknown location type: ${location?.type}`);
            break;
        }
      });

      console.log(neighborhoods, 'neighborhoods');

      // Process selected tags for interests
      selectedTags?.forEach((interest) => {
        if (interest?.id && interest?.name) {
          interests?.push({
            id: interest?.id,
            name: interest?.name,
            type: interest?.type,
          });
        }
      });

      const searchOptions = {
        adAccountId: adAccountId,
        gender: audienceData?.gender,
        minAge: audienceData?.minAge,
        maxAge: audienceData?.maxAge,
        selectedPlatforms: audienceData?.platforms,
        geoLocation: {
          cities,
          countries,
          regions,
          zips,
          places,
          custom_locations,
          geo_markets,
          electoral_districts,
          country_groups,
          subneighborhoods,
          neighborhoods,
          subcities,
          metro_areas,
          small_geo_areas,
          medium_geo_areas,
          large_geo_areas,
          location_cluster_ids,
        },
        advanceTargets: interests,
      };

      const response = await MetaAdsReachEstimate(authtoken, {
        adAccountId,
        searchOptions,
        isChildAdAccount: isChildAdAccount,
      });

      if (response?.flag && response?.data) {
        // Format the reach numbers to display as in the design
        const formattedReach = formatReachEstimate(response?.data?.estimatedMonthlyReachMin, response?.data?.estimatedMonthlyReachMax);
        setReachEstimate({
          users: formattedReach,
        });
      } else {
        toast.error(response?.message || 'Failed to get reach estimate', {
          position: 'top-center',
        });
      }

      const reachResponse = await MetaAdsAudienceEstimate(authtoken, {
        adAccountId,
        searchOptions,
        isChildAdAccount: isChildAdAccount,
      });

      if (reachResponse?.flag && reachResponse?.data) {
        // Format the reach numbers to display as in the design
        const formattedReach = formatReachEstimate(reachResponse?.data?.users_lower_bound, reachResponse?.data?.users_upper_bound);
        setAudienceEstimate({
          users: formattedReach,
          estimate_ready: reachResponse?.data?.estimate_ready,
        });
        setSectionCompleted({
          ...sectionCompleted,
          audience: true,
        });
        setExpandedSections({
          ...expandedSections,
          audience: false,
          openingMessage: true,
        });
      } else {
        toast.error(reachResponse?.message || 'Failed to get reach estimate', {
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('Error getting reach estimate:', error);
      toast.error(error?.message || 'Failed to get reach estimate', {
        position: 'top-center',
      });
    } finally {
      setIsLoadingReach(false);
    }
  };

  // const handleSubmit = () => {
  //     console.log("Ad data submitted", adData);
  //     navigate('/creator/metaads');
  // };

  const getMediaPreview = () => {
    if (!s3Link) return null;

    if (adData?.mediaType === 'Video') {
      return (
        <video
          src={s3Link}
          controls
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            borderRadius: '4px',
          }}
        />
      );
    } else {
      return (
        <img
          src={s3Link}
          alt="Uploaded Preview"
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            objectFit: 'contain',
            borderRadius: '4px',
          }}
        />
      );
    }
  };

  const getSelectedAccountName = () => {
    if (!selectedAccountId) return 'Facebook Page Name';
    const account = userAccounts?.find((acc) => acc?.id === selectedAccountId);
    return account?.name || 'Facebook Page Name';
  };

  return (
    <Box sx={{ bgcolor: '#f9f9f9', minHeight: '100vh', width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Tooltip title="Leads generated via Click-to-WhatsApp ads" arrow>
          <Typography variant="h5" fontWeight="bold" sx={{ cursor: 'default' }}>
            Create CTWA Ad
          </Typography>
        </Tooltip>

        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', pr: 2 }}>
          <img src={images.WalletIcon} style={{ width: 25, height: 25, marginRight: 6, paddingBottom: 3 }} />
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 16 }}>
            {localdata?.walletCurrency === 'INR' ? '₹' : '$'} {localdata?.metaWalletBalance?.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} onClick={() => toggleSection('adCreative')}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: sectionCompleted?.adCreative ? colors.primary03 : colors.primary03,
                  color: 'white',
                  mr: 1,
                }}
              >
                {sectionCompleted?.adCreative ? (
                  // Checkmark icon
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" fill="white" />
                  </svg>
                ) : (
                  // Number
                  '1'
                )}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ cursor: 'pointer', pr: 1, flex: 1 }}>
                Ad Creative
              </Typography>
              <ExpandMoreIcon
                sx={{
                  transform: expandedSections?.adCreative ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: colors.primary03,
                  cursor: 'pointer',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fill up your ad creative details. Ad will have similar UI shown in preview
            </Typography>
            {expandedSections?.adCreative && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Select User Account
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedAccountId}
                      onChange={(e) => setSelectedAccountId(e.target.value)}
                      displayEmpty
                      disabled={true}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                          color: '#666',
                        },
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e0e0e0',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Account
                      </MenuItem>
                      {userAccounts?.map((account) => (
                        <MenuItem key={account?.id} value={account?.id}>
                          Ch-2T {account?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Ad Name
                  </Typography>
                  <TextField
                    fullWidth
                    name="adName"
                    value={adData?.adName}
                    onChange={handleChange}
                    placeholder="Ad Name"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2, // Customize as needed
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover fieldset': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.primary03,
                        },
                      },
                    }}
                  />
                </Box>
                {/* <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>Ad Headline</Typography>
                                    <TextField
                                        fullWidth
                                        name="headline"
                                        value={adData?.headline}
                                        onChange={handleChange}
                                        placeholder="Ad Headline"
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2, // Customize as needed
                                                backgroundColor: 'white',
                                                '& fieldset': {
                                                    borderColor: '#CCCCCC',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: colors.primary03,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: colors.primary03,
                                                },
                                            },
                                        }}
                                    />
                                </Box> */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Ad Caption
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      name="adCaption"
                      value={adData?.adCaption || ''}
                      onChange={handleChange}
                      onClick={handleTextFieldClick}
                      onKeyUp={handleTextFieldKeyUp}
                      placeholder="Write your caption here... Use line breaks for better formatting"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#CCCCCC',
                          },
                          '&:hover fieldset': {
                            borderColor: colors.primary03,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary03,
                          },
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleEmojiButtonClick}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: colors.greys03,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <EmojiEmotionsIcon />
                    </IconButton>
                  </Box>

                  <Popover
                    open={Boolean(emojiAnchorEl)}
                    anchorEl={emojiAnchorEl}
                    onClose={handleEmojiClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <EmojiPicker onEmojiClick={handleEmojiClick} autoFocusSearch={false} theme="light" height={400} width={350} />
                  </Popover>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Footer
                  </Typography>
                  <TextField
                    fullWidth
                    name="footer"
                    value={adData?.footer}
                    onChange={handleChange}
                    placeholder="Footer text"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2, // Customize as needed
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover fieldset': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.primary03,
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Media type
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={adData?.mediaType}
                      name="mediaType"
                      onChange={handleChange}
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                      }}
                    >
                      <MenuItem value="Image">Image</MenuItem>
                      <MenuItem value="Video">Video</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {adData.mediaType === 'Image' ? (
                  // Single Image Upload Area (from your original code)
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Image
                    </Typography>
                    <Box
                      sx={{
                        border: `1px dashed ${dragActive ? colors.primary03 : '#ccc'}`,
                        borderRadius: 1,
                        p: 2,
                        textAlign: 'center',
                        height: s3Link ? 'auto' : 200,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'all 0.3s',
                        backgroundColor: dragActive ? 'rgba(110, 86, 207, 0.05)' : 'transparent',
                      }}
                      onDragEnter={handleDragFile}
                      onDragLeave={handleDragFile}
                      onDragOver={handleDragFile}
                      onDrop={handleDropFile}
                      onClick={() => {
                        if (!s3Link) {
                          upImageRef.current.click();
                        }
                      }}
                    >
                      {s3Link ? (
                        <Box sx={{ width: '100%', position: 'relative' }}>
                          <img
                            src={s3Link}
                            alt="Uploaded Preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                            }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: 2,
                              mt: 2,
                            }}
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              endIcon={<ReplayIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                upImageRef.current.click();
                              }}
                              style={{
                                textTransform: 'none',
                                color: 'black',
                                borderRadius: 8,
                                border: '1px solid black',
                                minWidth: '150px',
                                minHeight: '30px',
                              }}
                            >
                              Replace Image
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              endIcon={<RiDeleteBinLine />}
                              style={{
                                textTransform: 'none',
                                backgroundColor: '#FFDEDE',
                                color: '#E00000',
                                borderRadius: 8,
                                minWidth: '150px',
                                minHeight: '30px',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteMedia();
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              p: 3,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <img src={images.uploadImageIcon} width={24} height={24} />
                            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
                              Drag and drop image here
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              or click to browse from your computer
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                              Max file size: 5MB (JPG, PNG)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                              Recommended sizes: 1080×1080 or 1280×720
                            </Typography>
                          </Box>
                        </>
                      )}
                      <input type="file" ref={upImageRef} style={{ display: 'none' }} accept="image/*" onChange={handlefileupload} />
                    </Box>

                    {/* Image URL field */}
                    <Box sx={{ mb: 3, mt: 2 }}>
                      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ mr: 0.5, color: '#000000' }}>
                          Image URL
                        </Typography>
                        <Tooltip title="Copy a dedicated image address and paste here to fetch image directly" arrow placement="top">
                          <InfoOutlineIcon sx={{ fontSize: 14, color: '#000000', cursor: 'pointer' }} />
                        </Tooltip>
                      </Box>

                      <TextField
                        fullWidth
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="Paste image URL"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon fontSize="small" sx={{ color: colors.primary03 }} />
                            </InputAdornment>
                          ),
                          endAdornment: imageUrl && (
                            <InputAdornment position="end">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleImageUrl(imageUrl)}
                                sx={{
                                  backgroundColor: colors.primary03,
                                  '&:hover': {
                                    backgroundColor: '#5A46AF',
                                  },
                                }}
                              >
                                Fetch
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleImageUrl(imageUrl);
                          }
                        }}
                        disabled={!!s3Link && !imageUrl}
                      />
                    </Box>
                  </Box>
                ) : (
                  // Video + Thumbnail Upload Areas
                  <>
                    {/* Video Upload Area */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Video
                      </Typography>
                      <Box
                        sx={{
                          border: `1px dashed ${dragActive ? colors.primary03 : '#ccc'}`,
                          borderRadius: 1,
                          p: 2,
                          textAlign: 'center',
                          height: s3Link ? 'auto' : 200,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.3s',
                          backgroundColor: dragActive ? 'rgba(110, 86, 207, 0.05)' : 'transparent',
                        }}
                        onDragEnter={handleDragFile}
                        onDragLeave={handleDragFile}
                        onDragOver={handleDragFile}
                        onDrop={handleDropFile}
                        onClick={() => {
                          if (!s3Link) {
                            upImageRef.current.click();
                          }
                        }}
                      >
                        {s3Link ? (
                          <Box sx={{ width: '100%', position: 'relative' }}>
                            <video
                              src={s3Link}
                              controls
                              style={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: '4px',
                              }}
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 2,
                                mt: 2,
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ReplayIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  upImageRef.current.click();
                                }}
                                style={{
                                  textTransform: 'none',
                                  color: 'black',
                                  borderRadius: 8,
                                  border: '1px solid black',
                                  minWidth: '150px',
                                  minHeight: '30px',
                                }}
                              >
                                Replace Video
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                endIcon={<RiDeleteBinLine />}
                                style={{
                                  textTransform: 'none',
                                  backgroundColor: '#FFDEDE',
                                  color: '#E00000',
                                  borderRadius: 8,
                                  minWidth: '150px',
                                  minHeight: '30px',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteMedia();
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            <Box
                              sx={{
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <img src={images.metaAdsVideoIcon} alt="" />
                              <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
                                Drag or drop video here
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Max file size: 25MB
                              </Typography>
                            </Box>
                          </>
                        )}
                        <input type="file" ref={upImageRef} style={{ display: 'none' }} accept="video/*" onChange={handlefileupload} />
                      </Box>
                    </Box>

                    {/* Thumbnail Image Upload Area */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Thumbnail Image
                      </Typography>
                      <Box
                        sx={{
                          border: `1px dashed ${dragActive ? colors.primary03 : '#ccc'}`,
                          borderRadius: 1,
                          p: 2,
                          textAlign: 'center',
                          height: thumbnailS3Link ? 'auto' : 200,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'all 0.3s',
                          backgroundColor: dragActive ? 'rgba(110, 86, 207, 0.05)' : 'transparent',
                        }}
                        onClick={() => {
                          if (!thumbnailS3Link) {
                            thumbnailImageRef.current.click();
                          }
                        }}
                      >
                        {thumbnailS3Link ? (
                          <Box sx={{ width: '100%', position: 'relative' }}>
                            <img
                              src={thumbnailS3Link}
                              alt="Thumbnail Preview"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                              }}
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 2,
                                mt: 2,
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ReplayIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  thumbnailImageRef.current.click();
                                }}
                                style={{
                                  textTransform: 'none',
                                  color: 'black',
                                  borderRadius: 8,
                                  border: '1px solid black',
                                  minWidth: '150px',
                                  minHeight: '30px',
                                }}
                              >
                                Replace Thumbnail
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                endIcon={<RiDeleteBinLine />}
                                style={{
                                  textTransform: 'none',
                                  backgroundColor: '#FFDEDE',
                                  color: '#E00000',
                                  borderRadius: 8,
                                  minWidth: '150px',
                                  minHeight: '30px',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteThumbnail();
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            <Box
                              sx={{
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                              }}
                            >
                              <img src={images.uploadImageIcon} alt="" />
                              <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
                                Drag or drop thumbnail image here
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Max file size: 5MB
                              </Typography>
                            </Box>
                          </>
                        )}
                        <input type="file" ref={thumbnailImageRef} style={{ display: 'none' }} accept="image/*" onChange={handleThumbnailUpload} />
                      </Box>
                    </Box>

                    {/* Thumbnail URL Field */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Thumbnail Image URL
                      </Typography>
                      <TextField
                        fullWidth
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="Paste URL"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkIcon fontSize="small" sx={{ color: colors.primary03 }} />
                            </InputAdornment>
                          ),
                          endAdornment: imageUrl && (
                            <InputAdornment position="end">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleImageUrl(imageUrl)}
                                sx={{
                                  backgroundColor: colors.primary03,
                                  '&:hover': {
                                    backgroundColor: '#5A46AF',
                                  },
                                }}
                              >
                                Fetch
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleImageUrl(imageUrl);
                          }
                        }}
                        disabled={!!thumbnailS3Link && !imageUrl}
                      />
                    </Box>
                  </>
                )}
                <CheerioButton
                  borderStyle={{
                    borderWidth: 0,
                    borderRadius: 12,
                    paddingInline: 32,
                    width: '100%',
                  }}
                  backColor={'#8E53F7'}
                  textStyle={{ fontSize: 14, color: colors.white01 }}
                  btnText={'Next'}
                  onclick={handleNextClick}
                  disabled={!areRequiredFieldsFilled()}
                />
              </>
            )}
          </Paper>

          {/* Rest of the code remains unchanged */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} onClick={() => toggleSection('audience')}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: sectionCompleted.audience ? colors.primary03 : 'grey.300',
                  color: 'white',
                  mr: 1,
                }}
              >
                {sectionCompleted?.audience ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" fill="white" />
                  </svg>
                ) : (
                  '2'
                )}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ cursor: 'pointer', pr: 1, flex: 1 }}>
                Audience
              </Typography>
              <ExpandMoreIcon
                sx={{
                  transform: expandedSections?.audience ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: colors.primary03,
                  cursor: 'pointer',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an audience segment you want to target and reach out to
            </Typography>

            {expandedSections?.audience && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Select from saved audience
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedSavedAudience}
                      onChange={handleSavedAudienceChange}
                      displayEmpty
                      renderValue={(selected) => (selected ? savedAudiences.find((audience) => audience._id === selected)?.name : 'Select')}
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                      }}
                      IconComponent={(props) => <ExpandMoreIcon {...props} sx={{ color: colors.primary03 }} />}
                    >
                      <MenuItem value="">Select</MenuItem>
                      {savedAudiences?.map((audience) => (
                        <MenuItem key={audience?._id} value={audience?._id}>
                          {audience?.name} {audience?._id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <LocationSelector selectedLocations={selectedLocations} setSelectedLocations={setSelectedLocations} />

                <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Select gender
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        displayEmpty
                        value={audienceData?.gender} // Add this to track the value
                        onChange={(e) => setAudienceData((prev) => ({ ...prev, gender: e.target.value }))}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="ALL">All gender</MenuItem>
                        <MenuItem value="MALE">Male</MenuItem>
                        <MenuItem value="FEMALE">Female</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      From age
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select value={audienceData?.minAge} onChange={handleFromAgeChange} sx={{ borderRadius: 2 }}>
                        {[...Array(53)].map((_, i) => (
                          <MenuItem key={`from-age-${i + 13}`} value={i + 13}>
                            {i + 13}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      To age
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        To
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select value={audienceData?.maxAge} onChange={handleToAgeChange} sx={{ borderRadius: 2 }}>
                          {[...Array(53)].map((_, i) => (
                            <MenuItem key={`to-age-${i + 13}`} value={i + 13}>
                              {i + 13}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Choose Platform
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      displayEmpty
                      value={audienceData?.platforms}
                      onChange={handlePlatformChange}
                      renderValue={(selected) => {
                        if (selected?.length === 0) {
                          return 'Select platforms';
                        }

                        // Map platform values to display names
                        const platformMap = {
                          instagram: 'Instagram',
                          facebook: 'Facebook',
                        };

                        // Convert selected values to display names and join with commas
                        return selected?.map((value) => platformMap[value])?.join(', ');
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="instagram">
                        <Checkbox checked={audienceData?.platforms?.indexOf('instagram') > -1} />
                        <ListItemText primary="Instagram" />
                      </MenuItem>
                      <MenuItem value="facebook">
                        <Checkbox checked={audienceData?.platforms?.indexOf('facebook') > -1} />
                        <ListItemText primary="Facebook" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <AdvancedTargeting selectedTags={selectedTags} setSelectedTags={setSelectedTags} />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      textTransform: 'none',
                      border: '1px solid #ccc',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: colors.primary03,
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(0, 0, 0, 0.26)',
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                      },
                    }}
                    onClick={handleSaveAudienceName}
                  // disabled={!formIsValid}
                  >
                    Save Audience
                  </Button>

                  <CheerioButton
                    borderStyle={{
                      borderWidth: 0,
                      borderRadius: 12,
                      paddingInline: 32,
                      width: '100%',
                    }}
                    backColor={'#8E53F7'}
                    textStyle={{ fontSize: 14, color: colors.white01 }}
                    btnText={'Next'}
                    onclick={handleAudienceNext}
                  // disabled={isLoadingReach || (!formIsValid || !audienceSaved)}
                  />
                </Box>
                <Dialog
                  open={saveModalOpen}
                  onClose={handleCloseModal}
                  PaperProps={{
                    sx: {
                      borderRadius: 3,
                      p: 2,
                      maxWidth: '450px',
                      width: '100%',
                    },
                  }}
                >
                  <DialogTitle sx={{ p: 1 }}>Name audience</DialogTitle>
                  <DialogContent sx={{ p: 1 }}>
                    <TextField
                      autoFocus
                      margin="dense"
                      fullWidth
                      placeholder="Name"
                      value={audienceName}
                      onChange={(e) => setAudienceName(e.target.value)}
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </DialogContent>
                  <DialogActions sx={{ p: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Button
                      onClick={handleSaveAudience}
                      fullWidth
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        textTransform: 'none',
                        backgroundColor: '#8E53F7',
                        color: '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#7742F6',
                        },
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={handleCloseModal}
                      fullWidth
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        textTransform: 'none',
                        borderColor: '#cccccc',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: '#8E53F7',
                        },
                      }}
                    >
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} onClick={() => toggleSection('openingMessage')}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: sectionCompleted.openingMessage ? colors.primary03 : 'grey.300',
                  color: 'white',
                  mr: 1,
                }}
              >
                {sectionCompleted?.openingMessage ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" fill="white" />
                  </svg>
                ) : (
                  '3'
                )}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ cursor: 'pointer', pr: 1, flex: 1 }}>
                Opening Message
              </Typography>
              <ExpandMoreIcon
                sx={{
                  transform: expandedSections?.openingMessage ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: colors.primary03,
                  cursor: 'pointer',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a message to initiate conversation after people tap on the ad
            </Typography>
            {expandedSections?.openingMessage && (
              <>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 3 }}>
                    Message Destinations
                  </Typography>

                  <FormControl fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={selectedDestinations}
                      onChange={(e) => setSelectedDestinations(e.target.value)}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <Typography color="text.secondary">Option Name</Typography>;
                        }
                        return destinations
                          .filter((d) => selected.includes(d.id))
                          .map((d) => d.name)
                          .join(', ');
                      }}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: colors.primary03,
                        },
                      }}
                    >
                      {destinations.map((destination) => (
                        <MenuItem key={destination.id} value={destination.id}>
                          <Checkbox checked={selectedDestinations.includes(destination.id)} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '8px',
                                background: destination.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px',
                              }}
                            >
                              {destination.icon}
                            </Box>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                                {destination.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                {destination.subtitle}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mr: 0.5 }}>
                      Greeting Text
                    </Typography>
                    <Tooltip title="This text will be shown to users when they click on the ad and gets redirected to whatsapp" arrow placement="bottom">
                      <InfoOutlineIcon
                        sx={{
                          color: '#000000',
                          fontSize: 16,
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Add a text here"
                    size="small"
                    value={greetingText}
                    onChange={(e) => setGreetingText(e.target.value)}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover fieldset': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.primary03,
                        },
                      },
                    }}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mr: 0.5 }}>
                      Pre-filled message
                    </Typography>
                    <Tooltip
                      title="This message will be pre-filled for your leads to directly send on WhatsApp to help you identify that it's a lead from CTWA Ad"
                      arrow
                      placement="bottom"
                    >
                      <InfoOutlineIcon
                        sx={{
                          color: '#000000',
                          fontSize: 16,
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Add a message here"
                    size="small"
                    multiline
                    rows={4}
                    value={prefilledMessage}
                    onChange={(e) => {
                      if (e.target.value.length <= 80) {
                        setPrefilledMessage(e.target.value);
                      }
                    }}
                    helperText={`${prefilledMessage.length}/80 characters`}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover fieldset': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.primary03,
                        },
                      },
                    }}
                  />
                </Box>

                <CheerioButton
                  borderStyle={{
                    borderWidth: 0,
                    borderRadius: 12,
                    paddingInline: 32,
                    width: '100%',
                  }}
                  backColor={'#8E53F7'}
                  textStyle={{ fontSize: 14, color: colors.white01 }}
                  disabled={!greetingText.trim() || !prefilledMessage.trim() || !selectedDestinations.length}
                  btnText={'Next'}
                  onclick={() => {
                    if (greetingText.trim()) {
                      setSectionCompleted({
                        ...sectionCompleted,
                        openingMessage: true,
                      });
                      setExpandedSections({
                        ...expandedSections,
                        openingMessage: false,
                        budget: true,
                      });
                      // Move to next section or handle next action
                    }
                  }}
                />
              </>
            )}
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} onClick={() => toggleSection('budget')}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: sectionCompleted.openingMessage ? colors.primary03 : 'grey.300',
                  color: 'white',
                  mr: 1,
                }}
              >
                {sectionCompleted?.budget ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" fill="white" />
                  </svg>
                ) : (
                  '4'
                )}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ cursor: 'pointer', pr: 1, flex: 1 }}>
                Budget
              </Typography>
              <ExpandMoreIcon
                sx={{
                  transform: expandedSections?.budget ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: colors.primary03,
                  cursor: 'pointer',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add daily ad budget you would like to spend
            </Typography>
            {expandedSections?.budget && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Select per day budget
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="0.00"
                    size="small"
                    value={dailyBudget === '' ? '' : dailyBudget}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === '') {
                        setDailyBudget('');
                      } else {
                        const numericValue = Number(value);
                        setDailyBudget(numericValue);
                        if (numericValue >= minimumDailyBudget) {
                          eventsend('Ad_budget_clicked', { dailyBudget: numericValue });
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                    }}

                    inputProps={{
                      min: 0, // still allow deletion, we validate manually
                    }}
                    error={dailyBudget === '' || Number(dailyBudget) < minimumDailyBudget}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#CCCCCC',
                        },
                        '&:hover fieldset': {
                          borderColor: colors.primary03,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: colors.primary03,
                        },
                      },
                    }}
                  />

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Minimum daily budget: {currencySymbol}{minimumDailyBudget}
                  </Typography>

                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: '#F4F4F6',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}
                  >
                    <InfoOutlineIcon sx={{ color: '#000000', mr: 1 }} fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      You'll spend an average of {currencySymbol}{dailyBudget || minimumDailyBudget}.00 per day.
                      Your maximum daily spend is {currencySymbol}{Math.round((dailyBudget || minimumDailyBudget) * 1.2)}.00 and
                      your maximum weekly spend is {currencySymbol}{(dailyBudget || minimumDailyBudget) * 7}.00.{' '}
                      <Box
                        component="span"
                        sx={{ color: '#000000', fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setOpenBudgetInfoDialog(true)}
                      >
                        Learn more about daily budget
                      </Box>
                      <Dialog
                        open={openBudgetInfoDialog}
                        onClose={() => setOpenBudgetInfoDialog(false)}
                        fullWidth
                        maxWidth="lg" // You can use 'sm', 'md', 'lg', 'xl', or false for custom
                        PaperProps={{
                          sx: {
                            width: '700px', // Custom width
                            // maxWidth: '70vw',
                            borderRadius: 3,
                          },
                        }}
                      >
                        <DialogTitle
                          sx={{
                            fontWeight: 600,
                            fontSize: 20,
                            pb: 1,
                            borderBottom: 'none', // This removes the underline
                            position: 'relative',
                          }}
                        >
                          About Daily Budget
                          <IconButton
                            onClick={() => setOpenBudgetInfoDialog(false)}
                            sx={{
                              position: 'absolute',
                              right: 16,
                              top: 20,
                              p: 0, // Remove padding to make image snug
                            }}
                          >
                            <img src={images.CROSS_BLACK} alt="Close" width={20} height={20} />
                          </IconButton>
                        </DialogTitle>

                        <DialogContent sx={{ py: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                            <InfoOutlineIcon sx={{ color: '#000', mr: 1, mt: '2px' }} fontSize="small" />
                            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                              You can stop running an ad (and stop it from accruing additional charges) at any time by pausing it in Ads Manager. Note that you
                              may still be charged after pausing the ad. However, these charges are only for what you accrued before your ad stopped running.
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                            <InfoOutlineIcon sx={{ color: '#000', mr: 1, mt: '2px' }} fontSize="small" />
                            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                              When you set a daily budget, you're telling us the average amount that you want to spend each day. You can set a daily budget for
                              your overall campaign. This means that on days when better opportunities are available for you, we may spend up to 75% over your
                              daily budget on some days and less on others. On a weekly basis, we won't spend more than seven times your daily budget.
                            </Typography>
                          </Box>

                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
                            How Daily budget works
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                            <InfoOutlineIcon sx={{ color: '#000', mr: 1, mt: '2px' }} fontSize="small" />
                            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                              When you set your daily budget, you're setting an average daily spend over the course of a week. This means that every day, we'll
                              strive to get roughly your daily budget's worth of the result that you optimised for. However, there may be certain days when
                              better opportunities are available. On those days, we may spend up to 75% over your daily budget.
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                            We do this to deliver your ads and spend your budget as efficiently and effectively as possible. By allowing ourselves some
                            flexibility, we can more easily take advantage of opportunities when they arise.
                          </Typography>
                        </DialogContent>
                      </Dialog>
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Start Date
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={adStartDate}
                        onChange={(newValue) => setAdStartDate(newValue)}
                        minDate={new Date()}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            placeholder: 'dd/mm/yyyy',
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                                '& fieldset': {
                                  borderColor: '#CCCCCC',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.primary03,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.primary03,
                                },
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      End Date
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={adEndDate}
                        onChange={(newValue) => setAdEndDate(newValue)}
                        minDate={minEndDate}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            size: 'small',
                            placeholder: 'dd/mm/yyyy',
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                                '& fieldset': {
                                  borderColor: '#CCCCCC',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.primary03,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.primary03,
                                },
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>

                <CheerioButton
                  borderStyle={{
                    borderWidth: 0,
                    borderRadius: 12,
                    paddingInline: 32,
                    width: '100%',
                  }}
                  backColor={'#8E53F7'}
                  textStyle={{ fontSize: 14, color: colors.white01 }}
                  disabled={!adStartDate || !adEndDate}
                  btnText={'Next'}
                  onclick={() => {
                    if (adStartDate && adEndDate) {
                      setSectionCompleted({
                        ...sectionCompleted,
                        budget: true,
                      });
                      setExpandedSections({
                        ...expandedSections,
                        budget: false,
                        performanceOptimization: true,
                      });
                      // Move to next section or handle next action
                    }
                  }}
                />
              </>
            )}
          </Paper>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} onClick={() => toggleSection('performanceOptimization')}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: sectionCompleted.performanceOptimization ? colors.primary03 : 'grey.300',
                  color: 'white',
                  mr: 1,
                }}
              >
                {sectionCompleted?.performanceOptimization ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" fill="white" />
                  </svg>
                ) : (
                  '5'
                )}
              </Typography>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ cursor: 'pointer', pr: 1, flex: 1 }}>
                Performance Optimization
              </Typography>

              <ExpandMoreIcon
                sx={{
                  transform: expandedSections?.performanceOptimization ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: colors.primary03,
                  cursor: 'pointer',
                }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose ad objective to let us optimize results for your campaign
            </Typography>

            {expandedSections?.performanceOptimization && (
              <>
                <FormControl fullWidth>
                  <Select
                    displayEmpty
                    value={selectedObjective}
                    onChange={(e) => setSelectedObjective(e.target.value)}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Typography color="text.secondary">Choose objective</Typography>;
                      }
                      const option = objectives.find((obj) => obj.value === selected);
                      return option ? option.title : 'Choose objective';
                    }}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#CCCCCC',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.primary03,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colors.primary03,
                      },
                    }}
                  >
                    {objectives.map((obj) => (
                      <MenuItem
                        key={obj.value}
                        value={obj.value}
                        sx={{
                          alignItems: 'flex-start',
                          py: 1.5,
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 1,
                        }}
                      >
                        <Radio checked={selectedObjective === obj.value} sx={{ color: colors.primary03 }} />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                            {obj.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {obj.subtitle}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  sx={{
                    mt: 3,
                    mb: 3,
                    p: 2,
                    border: '1px solid #FBC02D',
                    backgroundColor: '#FFF8E1',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <InfoOutlineIcon sx={{ color: '#FBC02D', mt: '2px' }} />
                  <Typography variant="body2" sx={{ color: '#D49800', size: 16, mt: '3px', fontWeight: 400 }}>
                    You will need to fire a pixel event for this using workflow.{' '}
                    <a target="_blank" rel="noopener noreferrer" style={{ color: '#D49800', textDecoration: 'none' }}>
                      Learn more.
                    </a>
                  </Typography>
                </Box>

                <CheerioButton
                  borderStyle={{
                    borderWidth: 0,
                    borderRadius: 12,
                    paddingInline: 32,
                    width: '100%',
                  }}
                  backColor={'#8E53F7'}
                  textStyle={{ fontSize: 14, color: colors.white01 }}
                  disabled={!isFormValid}
                  btnText={'Publish'}
                  onclick={async () => {
                    if (isFormValid) {
                      try {
                        // Validate dates
                        const adjustedStartDate = new Date(adStartDate);
                        adjustedStartDate?.setHours(12, 0, 0, 0);
                        const adjustedEndDate = new Date(adEndDate);
                        adjustedEndDate?.setHours(12, 0, 0, 0);

                        if (adjustedEndDate < adjustedStartDate) {
                          toast.error('End date must be after start date', { position: 'top-center' });
                          return;
                        }

                        // Calculate number of days between start and end dates (inclusive)
                        const numberOfDays = Math.ceil((adjustedEndDate - adjustedStartDate) / (1000 * 60 * 60 * 24)) + 1;

                        // Check if user has enough balance
                        const totalBudgetRequired = dailyBudget * numberOfDays;

                        if (localdata?.metaWalletBalance < totalBudgetRequired || localdata?.metaWalletBalance <= 0) {
                          toast.error(
                            `Insufficient wallet balance. You need ${currencySymbol}${totalBudgetRequired.toFixed(2)} for this campaign.`,
                            { position: 'top-center' }
                          );
                          return;
                        }


                        // Mark budget section as complete
                        setSectionCompleted({
                          ...sectionCompleted,
                          performanceOptimization: true,
                        });

                        // Proceed with handleSubmit
                        await handleSubmit();
                      } catch (error) {
                        console.error('Error creating ad:', error);
                        toast.error(error.response?.data?.message || error.message || 'Failed to create ad', {
                          position: 'top-center',
                        });
                      }
                    }
                  }}
                />
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={3}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {' '}
            {/* Removed p: 2 */}
            {/* Header + Caption */}
            <Box sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                {/* Profile Info */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#E0E0E0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                    }}
                  >
                    {/* <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: '#666' }}>
                                            F
                                        </Typography> */}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#000000', lineHeight: 1.2 }}>{getSelectedAccountName()}</Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: '#666666',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      Sponsored
                      <CiGlobe />
                    </Typography>
                  </Box>
                </Box>

                {/* Options (3 dots) */}
                <Box sx={{ color: '#666666' }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>⋮</Typography>
                </Box>
              </Box>

              {/* Caption */}
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: 16,
                  color: '#000000',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                  textAlign: 'left',
                  mb: 1,
                }}
              >
                {adData?.adCaption || 'Your caption comes here!'}
              </Typography>
            </Box>
            {/* Media Section (no padding) */}
            {adData?.mediaType === 'Video' ? (
              s3Link ? (
                <Box
                  id="media-preview"
                  sx={{
                    width: '100%',
                    height: getAdaptiveHeight(),
                    backgroundColor: '#D9D9D9',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <video
                    src={s3Link}
                    controls
                    poster={thumbnailS3Link || undefined}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                    onLoadedMetadata={(e) => {
                      const video = e.target;
                      setMediaDimensions({ width: video.videoWidth, height: video.videoHeight });
                      setIsMediaLoaded(true);
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: getAdaptiveHeight(),
                    backgroundColor: '#D9D9D9',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {thumbnailS3Link ? (
                    <Box
                      component="img"
                      src={thumbnailS3Link}
                      alt="Thumbnail"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                      onLoad={(e) => {
                        const img = e.target;
                        setMediaDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                        setIsMediaLoaded(true);
                      }}
                    />
                  ) : (
                    <img
                      src={images.metaAdsVideoPreview}
                      alt="Video Placeholder"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  )}
                </Box>
              )
            ) : (
              <Box
                id="media-preview"
                sx={{
                  width: '100%',
                  height: getAdaptiveHeight(),
                  backgroundColor: '#D9D9D9',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                }}
              >
                {s3Link ? (
                  <Box
                    component="img"
                    src={s3Link}
                    alt="Uploaded Image"
                    sx={{
                      width: '100%',
                      height: 'auto', // 👈 Let image decide the height
                      objectFit: 'contain', // 👈 Show full image
                      display: 'block',
                    }}
                    onLoad={(e) => {
                      const img = e.target;
                      setMediaDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                      setIsMediaLoaded(true);
                    }}
                  />
                ) : (
                  <img
                    src={images.metaAdsImagePreview}
                    alt="Image Placeholder"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                )}
              </Box>
            )}
            {/* Chat on WhatsApp Section */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#F7F8FA',
                p: 1.5,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 14, color: '#000000' }}>
                  Chat on WhatsApp
                </Typography>
                <Tooltip title={adData?.footer || 'Footer Text'}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      maxWidth: '180px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}
                  >
                    {adData?.footer || 'Footer Text'}
                  </Typography>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#E2E5EC',
                  borderRadius: 1,
                  px: 2.5,
                  py: 1.0,
                }}
              >
                <FaWhatsapp style={{ fontSize: 16, marginRight: 6, marginBottom: 2 }} />

                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14 }}>
                  WhatsApp
                </Typography>
              </Box>
            </Box>
            {/* Like / Comment / Share */}
            <Box
              sx={{
                borderTop: '1px solid #E0E0E0',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                py: 1,
                px: 2,
                backgroundColor: '#fff',
              }}
            >
              {['Like', 'Comment', 'Share'].map((label) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                  }}
                >
                  {iconMap[label]}
                  <Typography variant="body2" sx={{ fontSize: 14, color: '#000000' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
          <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 16, color: '#000000' }}>
                Estimated Audience Size
              </Typography>

              <Box sx={{ mb: 1, mt: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#8E53F7' }}>
                  {isLoadingReach ? <CircularProgress size={16} /> : audienceEstimate ? audienceEstimate?.users || '-' : '-'}
                </Typography>
              </Box>

              {/* <Box sx={{ mb: 1 }}>
                                <Typography variant="body2">Leads</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {isLoadingReach ? (
                                        <CircularProgress size={16} />
                                    ) : reachEstimate ? (
                                        reachEstimate.leads || '-'
                                    ) : '-'}
                                </Typography>
                            </Box> */}
            </Box>
          </Paper>
          <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 16, color: '#000000' }}>
                Estimated results
              </Typography>

              <Box sx={{ mb: 1, mt: 1 }}>
                {/* <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 16, color: '#000000', mb: 1, mt: 1 }}>Audience Size</Typography> */}
                <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#8E53F7' }}>
                  {isLoadingReach ? <CircularProgress size={16} /> : reachEstimate ? reachEstimate?.users || '-' : '--'}
                </Typography>
              </Box>

              {/* <Box sx={{ mb: 1 }}>
                                <Typography variant="body2">Leads</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {isLoadingReach ? (
                                        <CircularProgress size={16} />
                                    ) : reachEstimate ? (
                                        reachEstimate.leads || '-'
                                    ) : '-'}
                                </Typography>
                            </Box> */}

              {/* <Typography variant="caption" sx={{ display: 'block', mt: 2, fontSize: 14, color: '#666666', }}>
                                The accuracy of estimates is based on factors such as your campaign data, the budget you entered, similar data, targeting criteria, and ad placements. Numbers are provided to give you an idea of performance for your selected budget and do not represent or guarantee results.
                            </Typography> */}
            </Box>
          </Paper>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              mt: 2,
              backgroundImage: `url(${images.WAPreviewImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: 16,
                  color: '#000000',
                  mb: 2,
                }}
              >
                Opening Message
              </Typography>
            </Box>

            {/* WhatsApp Chat Preview */}
            <Box
              sx={{
                bgcolor: 'rgba(245, 245, 245, 0.9)', // Make it semi-transparent to show background
                borderRadius: 2,
                p: 2,
                position: 'relative',
              }}
            >
              {/* Page Name Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  pb: 1,
                  borderBottom: '1px solid #E0E0E0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666666',
                    fontSize: 12,
                  }}
                >
                  {getSelectedAccountName()}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: '#666666',
                  fontSize: 11,
                  display: 'block',
                  mb: 1,
                }}
              >
                www.facebook.com/post...
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  color: '#000000',
                  mb: 2,
                }}
              >
                {greetingText || 'Hi! Please let us know how can we help you?'}
              </Typography>

              {/* Message Input Area */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'white',
                  borderRadius: 20,
                  p: 1,
                  border: '1px solid #E0E0E0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    color: '#999999',
                    flex: 1,
                    pl: 1,
                  }}
                >
                  {prefilledMessage || 'Message'}
                </Typography>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: '#25D366',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ml: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 'bold',
                    }}
                  >
                    ➤
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateMetaAd;
